// Mock data for CSED website

export const heroData = {
  headline: "Strengthening India's Development Architecture",
  subtext: "An apex, non-partisan, public-interest platform advancing economic stability, social equity, and environmental resilience through integrated, systems-level interventions.",
  image: "https://images.pexels.com/photos/31723766/pexels-photo-31723766.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
};

export const challengeData = {
  headline: "India's development challenges are interconnected—but solutions remain fragmented.",
  points: [
    {
      title: "Economic Volatility",
      description: "MSMEs face distress due to governance gaps and operational misalignments"
    },
    {
      title: "Social Inefficiencies",
      description: "Institutional capital underutilized due to weak monitoring frameworks"
    },
    {
      title: "Environmental Risks",
      description: "Climate risks increasingly affect economic stability and social systems"
    }
  ]
};

export const pillarsData = [
  {
    id: 1,
    title: "Economic Stability",
    subtitle: "Rehabilitating and Future-Proofing Viable Assets",
    description: "Addressing structural risks in MSMEs and regionally significant firms through governance and operational interventions. The objective is structural correction, not capital injection.",
    approach: "Evidence-based diagnostics, non-financial advisory, strategic alignment support, and institutional risk assessment to stabilize viable assets.",
    outcome: "Stabilized enterprises, protected employment, supply chain continuity, and strengthened regional economies.",
    image: "https://images.pexels.com/photos/18920790/pexels-photo-18920790.jpeg",
    icon: "TrendingUp"
  },
  {
    id: 2,
    title: "Social Equity",
    subtitle: "Ensuring Accountable and Measurable Impact",
    description: "CSED functions as a Trusted Guardian of Institutional Resources, bridging the gap between funders demanding transparency and grassroots organizations requiring capacity strengthening.",
    approach: "High-credibility M&E frameworks, institutional governance capacity building, outcome-based reporting, and financial compliance support.",
    outcome: "Efficient and transparent capital deployment, measurable social impact, and enhanced accountability across the development ecosystem.",
    image: "https://images.pexels.com/photos/19883685/pexels-photo-19883685.jpeg",
    icon: "Users"
  },
  {
    id: 3,
    title: "Environmental Resilience",
    subtitle: "Embedding Risk Awareness into Economic Governance",
    description: "Treating environmental sustainability as a systemic governance issue. Integrating climate volatility, resource scarcity, and sustainability considerations into economic and social planning.",
    approach: "Climate risk diagnostics, circular economy frameworks, regional resource balance assessment, and sustainability governance integration through nature-based solutions including afforestation and green cover expansion.",
    outcome: "Resilient systems that proactively integrate environmental considerations into development planning and economic decision-making.",
    image: "https://images.pexels.com/photos/36523817/pexels-photo-36523817.jpeg",
    icon: "Leaf"
  }
];

export const roleData = {
  headline: "An Institutional Stabiliser & Fiduciary Platform",
  description: "CSED operates at the systems level to strengthen the connective architecture between policy formulation, capital deployment, enterprise governance, grassroots implementation, and measurable outcomes.",
  tagline: "Responsible Growth through Institutional Integrity",
  functions: [
    {
      title: "Specialized Research & Non-Financial Advisory",
      description: "Evidence-based insights and structural advisory for policy, institutional decision-making, and enterprise governance",
      icon: "BookOpen"
    },
    {
      title: "High-Credibility M&E",
      description: "Robust monitoring and evaluation frameworks ensuring transparent, accountable, and measurable impact",
      icon: "BarChart"
    },
    {
      title: "Apex Convening & Policy Advocacy",
      description: "Structured stakeholder engagement and policy dialogue to address systemic challenges and improve institutional effectiveness",
      icon: "MessageSquare"
    }
  ]
};

export const insightsData = {
  headline: "Featured Insights",
  publications: [
    {
      id: 1,
      title: "An Integrated Framework for Economic Stability, Social Equity, and Environmental Resilience",
      type: "Institutional Position Paper",
      description: "A comprehensive framework addressing India's interconnected development challenges through systemic solutions.",
      date: "December 2024",
      downloadUrl: "#"
    },
    {
      id: 2,
      title: "Strengthening MSME Governance: A Systems Approach",
      type: "Research Report",
      description: "Analysis of governance challenges facing MSMEs and structured interventions for enterprise stabilization.",
      date: "November 2024",
      downloadUrl: "#"
    },
    {
      id: 3,
      title: "Monitoring & Evaluation: Building Accountability in CSR Capital Deployment",
      type: "Policy Brief",
      description: "Framework for enhancing transparency and measurable impact in institutional funding.",
      date: "October 2024",
      downloadUrl: "#"
    }
  ]
};

export const partnersData = {
  headline: "Who We Work With",
  description: "CSED collaborates with institutions across sectors to strengthen development systems.",
  partnerTypes: [
    {
      type: "Government Bodies",
      description: "Policy support and institutional strengthening",
      icon: "Building"
    },
    {
      type: "Social Development Organizations/NPOs",
      description: "Capacity building and M&E support for grassroots implementation",
      icon: "Users"
    },
    {
      type: "CSR & Institutional Funders",
      description: "Monitoring, evaluation, and impact assurance",
      icon: "Briefcase"
    },
    {
      type: "Enterprises",
      description: "Governance and strategic alignment",
      icon: "Factory"
    },
    {
      type: "Academic Institutions",
      description: "Research and knowledge collaboration",
      icon: "GraduationCap"
    }
  ]
};

export const aboutData = {
  overview: "CSED is an apex, non-partisan, public-interest body operating as a Section 8 non-profit institution with a long-horizon mandate to strengthen India's development architecture through the integrated advancement of Economic Stability, Social Equity, and Environmental Resilience.",
  vision: "To enable a resilient, equitable, and sustainable development ecosystem through institutional strengthening and systemic alignment.",
  mission: "To enhance the effectiveness of development systems by improving governance capacity, strengthening capital deployment frameworks, and enabling measurable outcomes across economic, social, and environmental domains.",
  positioning: "CSED operates at the systems level as an institutional stabiliser and fiduciary advisory platform. CSED is distinct from implementing NGOs (which operate at grassroots level), commercial consultancies (which operate on transactional mandates), and trade lobbies (which represent sectoral interests).",
  values: [
    "Institutional Integrity",
    "Evidence-Based Approach",
    "Systems-Level Thinking",
    "Long-Horizon Impact",
    "Fiduciary Accountability",
    "Non-Partisan Commitment"
  ]
};

export const meData = {
  headline: "Monitoring & Evaluation",
  subheadline: "Building Accountability Through Structured M&E",
  overview: "CSED provides high-credibility M&E frameworks for efficient capital deployment and measurable outcomes.",
  methodology: [
    "Structured impact measurement",
    "Continuous monitoring systems",
    "Independent evaluation",
    "Outcome validation"
  ],
  valueToStakeholders: {
    funders: ["Transparency", "Accountability", "Risk Mitigation"],
    implementers: ["Capacity Strengthening", "Governance Support"]
  }
};

export const policyData = {
  headline: "Policy & Advocacy",
  overview: "CSED facilitates structured stakeholder engagement to address systemic challenges and improve policy effectiveness.",
  areas: [
    "Economic systems and enterprise resilience",
    "Social impact frameworks",
    "Environmental risk integration"
  ],
  approach: "Research → Demonstration → Advocacy → Institutional Feedback"
};

export const navigationLinks = [
  { name: "Home", path: "/" },
  { name: "Research Library", path: "/research-library" },
  { name: "Expert Panel", path: "/monitoring" },
  { name: "Events", path: "/events" },
  { name: "Market Place", path: "/marketplace" },
  { name: "Policy & Advocacy", path: "/policy" },
  { name: "Governance", path: "/governance" }
];

// Additional links shown only to logged-in members
export const memberOnlyLinks = [
  { name: "Engage", path: "/engage" },
  { name: "Members Directory", path: "/members" }
];

export const contactData = {
  email: "info@thecsed.org",
  phone: "+91 (0) 9840365962",
  address: "Chamber for Socio Economic Development, Plot#7, Phase - 1, VGN CH 40, Shivani Street, Anna Nagar, Chennai - 600040, Tamil Nadu, INDIA."
};

export const governanceData = {
  overview: "CSED's governance structure ensures institutional integrity, accountability, and strategic oversight. Our multi-layered governance framework comprises the Board of Directors, Governing Council, Advisory Board, and the Executive Secretariat, each with distinct roles and responsibilities.",
  
  board: [
    {
      id: 1,
      name: "Stella Franklin",
      role: "Chairperson & Founding Director",
      photo: "/images/governance/stella-franklin.png?v=2",
      profile: "Stella Franklin serves as Chairperson and Founding Director of the Chamber for Socio Economic Development (CSED), where she provides governance stewardship, ethical oversight, and strategic direction to the institution's mission. A healthcare professional by training and practice, Stella brings a people-centred and outcome-oriented perspective to institutional leadership. Her experience in clinical and community-oriented environments has shaped her strong commitment to measurable impact, accountability, and structured service delivery. As Chairperson, Stella plays a critical role in strengthening board governance, ensuring fiduciary discipline, and embedding social equity considerations into CSED's strategic initiatives. She advocates for development models that balance economic growth with community well-being and institutional transparency. Within CSED's three-pillar framework, Stella anchors the Social Equity dimension, ensuring that enterprise stability and environmental resilience ultimately translate into human-centred outcomes. Her leadership priorities include ethical governance and compliance discipline, institutional transparency and accountability, social impact alignment, board oversight and long-term stability, and ensuring values-driven growth. She believes that responsible growth begins with people — and that institutional integrity is the foundation of lasting development."
    },
    {
      id: 2,
      name: "Xavier DL",
      role: "Founding Director",
      photo: "/images/governance/xavier-dl.png?v=4",
      profile: "Xavier DL is the Founder and Strategic Architect of the Chamber for Socio Economic Development (CSED), a Section 8 institutional platform dedicated to advancing Economic Stability, Social Equity, and Environmental Resilience through structured dialogue, research, and governance frameworks. With a background in social development and institutional leadership, Xavier combines systems thinking with operational discipline. His professional journey reflects sustained engagement in organisational structuring, stakeholder engagement, and enterprise development, with a strong focus on governance integrity and long-term institutional credibility. As the driving force behind CSED's formation, Xavier led the incorporation process, designed its governance architecture, established its brand identity framework, and conceptualised the Enterprise & Policy Dialogue Series in collaboration with leading institutional partners. He is currently spearheading the development of CSED's Monitoring, Evaluation, and Rating capability aimed at strengthening capital accountability and enterprise stability across sectors. Xavier's leadership philosophy is grounded in structural thinking rather than reactive commentary. He believes that economic volatility, livelihood fragility, and environmental stress must be addressed through integrative institutional design rather than fragmented interventions. His vision is to build CSED into a generational institution that works quietly, credibly, and consistently in the national interest."
    },
    {
      id: 3,
      name: "Independent Director",
      role: "Independent Director",
      photo: null,
      profile: "Brings independent perspective and ensures governance best practices. Provides objective oversight on strategic decisions and institutional policies."
    },
    {
      id: 4,
      name: "Independent Director",
      role: "Independent Director",
      photo: null,
      profile: "Ensures transparency and accountability in governance processes. Provides independent judgment on matters of fiduciary responsibility and institutional integrity."
    },
    {
      id: 5,
      name: "Independent Director",
      role: "Independent Director",
      photo: null,
      profile: "Strengthens Board independence and brings diverse expertise. Safeguards against mission drift and ensures long-term institutional sustainability."
    }
  ],
  
  governanceCouncil: [
    {
      id: 1,
      name: "Governing Council Member",
      role: "Chairperson",
      photo: null,
      profile: "Senior leader with extensive experience in policy formulation, institutional development, and economic reforms. Brings decades of expertise in governance and fiscal policy."
    },
    {
      id: 2,
      name: "Governing Council Member",
      role: "Member",
      photo: null,
      profile: "Distinguished professional with expertise in social impact investing and institutional philanthropy. Serves on boards of multiple development organizations."
    },
    {
      id: 3,
      name: "Governing Council Member",
      role: "Member",
      photo: null,
      profile: "Renowned expert in development economics with extensive publications on inclusive growth and social equity frameworks. Advisor to state governments."
    },
    {
      id: 4,
      name: "Governing Council Member",
      role: "Member",
      photo: null,
      profile: "Senior financial sector leader with expertise in enterprise governance and financial inclusion. Decades of experience in institutional finance."
    },
    {
      id: 5,
      name: "Governing Council Member",
      role: "Member",
      photo: null,
      profile: "Environmental policy expert specializing in climate resilience and sustainability. Former advisor on national environmental initiatives."
    }
  ],
  
  advisoryBoard: [
    {
      id: 1,
      name: "Advisory Board Member",
      role: "Senior Advisor",
      photo: null,
      profile: "Distinguished public service professional with extensive experience in institutional capacity building and governance reforms at state and national levels."
    },
    {
      id: 2,
      name: "Advisory Board Member",
      role: "Research Advisor",
      photo: null,
      profile: "Leading researcher specializing in social equity and monitoring & evaluation frameworks. Published extensively on development impact and systems strengthening."
    },
    {
      id: 3,
      name: "Advisory Board Member",
      role: "Industry Advisor",
      photo: null,
      profile: "Senior business leader championing MSME development and circular economy practices. Extensive experience driving sustainable business models."
    },
    {
      id: 4,
      name: "Advisory Board Member",
      role: "Academic Advisor",
      photo: null,
      profile: "Academic leader specializing in policy analysis and institutional economics. Consultant to international development organizations."
    },
    {
      id: 5,
      name: "Advisory Board Member",
      role: "Strategic Advisor",
      photo: null,
      profile: "Senior strategy professional specializing in organizational transformation and systems thinking. Mentor to multiple social enterprises."
    }
  ],
  
  secretariat: {
    directorGeneral: {
      name: "Xavier DL",
      role: "Director General",
      photo: "/images/governance/xavier-dl.png?v=4",
      profile: "Xavier DL serves as Director General of CSED, providing strategic leadership and operational oversight to the institution's mission. With a background in social development and institutional leadership, he combines systems thinking with operational discipline. As Director General, Xavier leads CSED's day-to-day operations, strategic partnerships, and institutional development initiatives. He is spearheading the development of CSED's Monitoring, Evaluation, and Rating capability aimed at strengthening capital accountability and enterprise stability across sectors. His leadership philosophy is grounded in structural thinking, believing that economic volatility, livelihood fragility, and environmental stress must be addressed through integrative institutional design rather than fragmented interventions."
    },
    departments: [
      {
        name: "Operations",
        role: "Institutional Backbone",
        description: "Manages institutional systems, partnership management, event logistics, and administrative governance support. Ensures institutional discipline and compliance.",
        icon: "Settings"
      },
      {
        name: "Programs & Research",
        role: "Intellectual Engine",
        description: "CSED's core differentiator. Conducts research, designs M&E frameworks, validates field interventions, and produces advocacy outputs including policy briefs and position papers.",
        icon: "BookOpen"
      },
      {
        name: "Finance & Administration",
        role: "Fiduciary Management",
        description: "Ensures financial oversight, budget management, statutory compliance, and transparent fund utilization in accordance with Section 8 regulations.",
        icon: "Calculator"
      }
    ]
  }
};
